

import { User } from 'firebase/auth';

export type MenuItem = {
    id: string;
    name: string;
    price: number;
    category: string;
    imageUrl: string | null;
    dataAiHint: string | null;
    description?: string | null;
    calories?: number | null;
    allergenFlags?: Partial<Record<Allergen, boolean>>;
    modifiers?: ModifierGroup[];
    notes?: string | null;
    storeId?: string;
    isUpsellPromo?: boolean;
    upsellPromoDiscountPercent?: number | null;
};
  
export type OrderItem = {
    id: string; // Unique instance ID in the cart
    menuItemId: string;
    name: string;
    quantity: number;
    unitPrice: number;
    originalUnitPrice: number | null;
    totalPrice: number;
    isUpsellDiscounted?: boolean;
    selectedModifiers?: {
      groupName: string;
      optionName: string;
      priceAdjustment: number;
    }[];
    rawSelectedModifiers?: Record<string, string | string[]>; // For re-opening modifier modal
    imageUrl: string | null;
    dataAiHint: string | null;
    allergenFlags: Partial<Record<Allergen, boolean>>;
    notes?: string | null;
    status?: 'pending' | 'ready' | 'bumped';
};

export type MenuCategory = {
    id: string;
    name: string;
    description: string;
};

export type TableStatus = 'Available' | 'Occupied' | 'Reserved' | 'NeedsCleaning';
export const TABLE_STATUS_LIST: TableStatus[] = ['Available', 'Occupied', 'Reserved', 'NeedsCleaning'];

export type RestaurantArea = {
    id: string;
    name: string;
    storeId?: string;
};

export type OrderType = 'pos_takeaway' | 'pos_table_dinein' | 'kiosk_takeaway' | 'kiosk_dinein' | 'delivery' | 'online';

export type DeliveryDetails = {
    customerName: string;
    customerPhone: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postcode: string;
    sanitizedPostcode?: string;
};

export type Order = {
    id: string;
    orderNumber: string;
    items: OrderItem[];
    subtotal: number;
    tax: number;
    deliveryCharge?: number;
    total: number;
    status: 'pending' | 'completed' | 'cancelled' | 'awaiting_payment' | 'parked' | 'ready_for_pickup' | 'out_for_delivery';
    timestamp: number;
    updatedAt: number;
    paymentMethod: PaymentMethod;
    orderType: OrderType;
    tableId?: string | null;
    tableName?: string | null;
    customerNotes?: string | null;
    clientName?: string | null;
    clientPhone?: string | null;
    storeId?: string;
    voidReason?: string | null;
    voidedBy?: {
        uid: string;
        name: string | null;
    } | null;
    deliveryDetails?: DeliveryDetails | null;
    driver?: {
        uid: string;
        name: string | null;
    } | null;
    isNewlyAdded?: boolean;
};

export type PaymentMethod = 'cash' | 'card' | 'mobile' | null;

export type ModifierOption = {
    id: string;
    name: string;
    priceAdjustment: number;
    isDefault?: boolean;
};

export type ModifierGroup = {
    id: string;
    name: string;
    type: 'radio' | 'checkbox';
    options: ModifierOption[];
    minSelection?: number;
    maxSelection?: number;
};

export type MenuCategoryUIDefinition = {
    id: string | number;
    name: string;
    colorHex: string;
    imageUrl?: string | null; // Modified to allow null
    dataAiHint?: string;
    storeId?: string;
};

export const ALLERGEN_LIST = ['celery', 'gluten', 'crustaceans', 'eggs', 'fish', 'lupin', 'milk', 'molluscs', 'mustard', 'peanuts', 'sesame', 'soya', 'sulphites', 'treeNuts'] as const;
export type Allergen = typeof ALLERGEN_LIST[number];

export const ALLERGEN_DISPLAY_NAMES: Record<Allergen, string> = {
  celery: 'Celery',
  gluten: 'Gluten',
  crustaceans: 'Crustaceans',
  eggs: 'Eggs',
  fish: 'Fish',
  lupin: 'Lupin',
  milk: 'Milk',
  molluscs: 'Molluscs',
  mustard: 'Mustard',
  peanuts: 'Peanuts',
  sesame: 'Sesame',
  soya: 'Soya',
  sulphites: 'Sulphites',
  treeNuts: 'Tree Nuts',
};

export type AllergenicItemDetail = {
  orderItemId: string;
  name: string;
  allergens: string[];
};

export type CheckAllergenWarningInput = {
    orderItems: { name: string; id: string }[];
    targetAllergens: Allergen[];
};

export type CheckAllergenWarningOutput = {
    hasWarning: boolean;
    warnings: string[];
    allergenicItems: AllergenicItemDetail[];
};

export type SuggestedUpsellItem = {
    id: string;
    name: string;
    price: number;
};

export type UpsellRecommendationsInput = {
    orderHistory: { items: { name: string, category: string }[] }[];
    currentOrder: string;
    availableMenuItems: { 
        id: string; 
        name: string; 
        price: number; 
        category: string;
        isUpsellPromo?: boolean;
        upsellPromoDiscountPercent?: number;
    }[];
    suggestionFocus?: 'general' | 'meal_completion' | 'drinks_and_desserts';
};

export type UpsellRecommendationsOutput = {
    upsellRecommendationMessage: string;
    suggestedItems: SuggestedUpsellItem[];
    confidenceScore: number;
};

export type KioskPaymentMethods = 'cash' | 'card' | 'both';

export type DeliveryZone = {
  id: string;
  name: string; // e.g. "Local Postcodes" or "Within 5km"
  type: 'postcode' | 'radius';
  charge: number;
  // for postcode type
  postcodePrefix?: string;
  // for radius type
  radiusKm?: number;
};

export type ReceiptSectionSetting = {
  show: boolean;
  fontSize: number;
};

export type ReceiptTemplateSettings = {
  showHeader: ReceiptSectionSetting;
  showOnPremiseNumber: ReceiptSectionSetting;
  showOrderDetails: ReceiptSectionSetting;
  showItems: ReceiptSectionSetting;
  showClientComment: ReceiptSectionSetting;
  showPaidStatus: ReceiptSectionSetting;
  showPackagingControl: ReceiptSectionSetting;
  showQRCode: ReceiptSectionSetting;
};

export const createDefaultReceiptSettings = (): ReceiptTemplateSettings => ({
    showHeader: { show: true, fontSize: 14 },
    showOnPremiseNumber: { show: true, fontSize: 16 },
    showOrderDetails: { show: true, fontSize: 12 },
    showItems: { show: true, fontSize: 14 },
    showClientComment: { show: true, fontSize: 12 },
    showPaidStatus: { show: true, fontSize: 12 },
    showPackagingControl: { show: true, fontSize: 12 },
    showQRCode: { show: true, fontSize: 12 },
});


export type ShopInfoState = {
    id: string; // Document ID will be storeId
    storeId: string;
    shopName: string;
    addressStreet: string;
    addressCity: string;
    addressPostcode: string;
    addressCountry: string;
    contactPhone: string;
    contactEmail: string;
    website: string;
    taxId: string;
    taxRate: string;
    pricesIncludeTax: boolean;
    receiptHeader: string;
    receiptFooter: string;
    showLogoOnReceipt: boolean;
    currencySymbol: string;
    currencyCode: string;
    openingHours: string;
    exitKioskPin: string;
    kioskAllowedPaymentMethods: KioskPaymentMethods;
    minimumDeliveryOrderValue: number;
    deliveryZones: DeliveryZone[];
    storeLatitude: number;
    storeLongitude: number;
    clientReceiptSettings: ReceiptTemplateSettings;
    kitchenReceiptSettings: ReceiptTemplateSettings;
};

export const createDefaultShopInfoState = (): ShopInfoState => ({
  id: '',
  storeId: '',
  shopName: 'OrderPoint®️ Eatery',
  addressStreet: '123 Main Street',
  addressCity: 'Foodville',
  addressPostcode: 'FV1 2AB',
  addressCountry: 'United Kingdom',
  contactPhone: '01234 567890',
  contactEmail: 'contact@orderpoint.com',
  website: 'https://www.orderpoint.com',
  taxRate: '20',
  taxId: 'GB123456789',
  pricesIncludeTax: true,
  receiptHeader: 'Thank you for dining at OrderPoint®️ Eatery!',
  receiptFooter: 'Find us online: www.orderpoint.com\nFollow us @OrderPoint',
  showLogoOnReceipt: true,
  currencySymbol: '£',
  currencyCode: 'GBP',
  openingHours: 'Mon-Fri: 9am - 10pm\nSat: 10am - 11pm\nSun: 10am - 9pm',
  exitKioskPin: '786011',
  kioskAllowedPaymentMethods: 'both',
  minimumDeliveryOrderValue: 0,
  deliveryZones: [],
  storeLatitude: 51.5074,
  storeLongitude: -0.1278,
  clientReceiptSettings: createDefaultReceiptSettings(),
  kitchenReceiptSettings: createDefaultReceiptSettings(),
});


export type RestaurantTable = {
    id: string;
    name: string;
    capacity: number;
    status: TableStatus;
    currentOrderId?: string | null;
    currentOrderNumber?: string | null;
    currentOrderTotal?: number | null;
    currentOrderTimestamp?: number | null;
    storeId?: string;
    notes?: string | null;
    areaId?: string | null;
    x?: number;
    y?: number;
};

export type UserRole = 'Admin' | 'Manager' | 'Staff' | 'Driver';

export interface AppUser {
    uid: string;
    email: string | null;
    fullName: string | null;
    accessibleStores: Record<string, UserRole>; // e.g. { 'store-id-1': 'Admin', 'store-id-2': 'Staff' }
    pin?: string;
}

export interface Store {
    id: string;
    name: string;
    ownerId: string;
}

export type DenominationInfo = {
  id: string;
  name: string;
  value: number;
  quantity: number;
};

export type PayoutInfo = {
  id: string;
  description: string;
  amount: number;
};

export type CashUpSession = {
  id: string;
  finalizedAt: number;
  storeId: string;
  date: string;
  startingFloat: number;
  cashDenominations: DenominationInfo[];
  totalCashCounted: number;
  payouts: PayoutInfo[];
  totalPayouts: number;
  reportedCashSalesPOS: number;
  reportedCardSales: number;
  reportedOtherSales: number;
  totalReportedTakings: number;
  netCashInTillActual: number;
  expectedCashInTill: number;
  difference: number;
  notes?: string;
};

export type EstimateWaitTimeInput = {
    ordersInQueue: {
        orderNumber: string;
        itemCount: number;
    }[];
};

export type EstimateWaitTimeOutput = {
    estimatedWaitTime: string;
};


interface PrintBridge {
  printText: (printerKey: 'receiptPrinter' | 'kitchenPrinter', escposMarkup: string) => void;
  openPrinterSelection: () => void;
  getPrinterAddress: (key: 'receiptPrinter' | 'kitchenPrinter') => string;
}

interface TeyaDeviceBridge {
  startPayment: (orderId: string, amount: number, currencyCode: string) => void;
}

declare global {
  interface Window {
    PrintBridge?: PrintBridge;
    TeyaDeviceBridge?: TeyaDeviceBridge;
  }
}
